import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal, computed, ViewChild, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { AgentTriggerComponent } from 'src/app/shared/common-ui-components/agent-trigger/agent-trigger.component';
import { UIIconsComponent } from 'src/app/shared/ui/icons/icons.component';
import { CardsContainerComponent } from '../cards-container/cards-container.component';
import { CanvasContentService } from '../../canvas-content.service';
import { CanvasActionsComponent } from '../canvas-actions/canvas-actions.component';
import { AddContentModalComponent } from '../add-content-modal/add-content-modal.component';
import { AssetsApiService } from 'src/app/core/http/services/assets.service';
import { CopilotStorageService } from 'src/app/shared/services/copilot-storage.service';
import { finalize } from 'rxjs';
import { cloneDeep } from 'lodash';
import { DataCopilotApiService } from 'src/app/core/http/services/data-copilot.service';
import { DatePipe } from '@angular/common';

@Component({
  selector: 'app-create-canvas',
  standalone: true,
  providers: [DatePipe],
  imports: [
    UIIconsComponent,
    AgentTriggerComponent,
    CardsContainerComponent,
    CanvasActionsComponent,
    AddContentModalComponent
  ],
  templateUrl: './create-canvas.component.html',
  styleUrl: './create-canvas.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CreateCanvasComponent {
  private router = inject(Router);
  private canvasContentService = inject(CanvasContentService);
  private assetsApiService = inject(AssetsApiService);
  private copilotStorageService = inject(CopilotStorageService);
  private dataCopilotService = inject(DataCopilotApiService);
  private datePipe = inject(DatePipe);

  showAddContentModal = signal<boolean>(true);
  isActionOpen = signal<boolean>(false);
  canvasId: string = '';
  canvasName: string = '';
  canvasDescription: string = '';
  isLoading = signal<boolean>(false);
  loadError = signal<boolean>(false);
  selectedReportIds = signal<string[]>([]);
  selectedAnswerIds = signal<string[]>([]);
  conversations = signal<{ questions: any[], answers: any[] }>({ questions: [], answers: [] });
  freeFormCards = signal<{ cardId: string, assetId: string, content: string }[]>([]);
  unsavedFreeFormCards = signal<{ cardId: string, hasUnsavedChanges: boolean }[]>([]);
  loadingFreeFormCards: Set<string> = new Set();
  addedReportIds = signal<Set<string>>(new Set<string>());
  addedQuestionIds = signal<Set<string>>(new Set<string>());

  @ViewChild(CardsContainerComponent) cardsContainer!: CardsContainerComponent;
  @ViewChild(CanvasActionsComponent) canvasActions!: CanvasActionsComponent;

  // Signal queries used only for reactive computed() — imperative code uses @ViewChild above
  private _cardsContainerSignal = viewChild(CardsContainerComponent);
  private _canvasActionsSignal  = viewChild(CanvasActionsComponent);

  // computed() ensures OnPush re-renders when child signals change
  isCanvasLocked   = computed(() => this._canvasActionsSignal()?.isCanvasLocked() ?? false);
  isMaxCardsReached = computed(() => (this._cardsContainerSignal()?.cards()?.length ?? 0) >= 5);

  private destroyRef = inject(DestroyRef);

  constructor(private route: ActivatedRoute) {
    this.route.params
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(params => {
        if (params['id']) {
          this.canvasId = params['id'];
          this.loadCanvasData(this.canvasId);
        } else {
          this.showAddContentModal.set(true);
        }
      });
  }

  // Method to get latest cards with updated positions before saving
  getLatestCardsForSave(): any[] {
    if (this.cardsContainer) {
      return this.cardsContainer.updateAllCardPositions();
    }
    return [];
  }

  ngOnInit(): void {
    if (!this.canvasId) {
      this.showAddContentModal.set(true);
    }
  }

  onClickBack(): void {
    this.router.navigate(['/dashboard/canvas']);
  }

  addCard(): void {
    if (!this.isCanvasLocked() && !this.isMaxCardsReached()) {
      this.showAddContentModal.set(true);
    }
  }

  closeAddContentModal(): void {
    this.showAddContentModal.set(false);
  }

  refreshGridster(): void {
    if (this.cardsContainer) {
      if (this.canvasId) {
        this.loadCanvasData(this.canvasId);
      }
    }
  }

  onToggleLockCanvas(isLocked: boolean): void {
    if (this.cardsContainer) {
      this.cardsContainer.options = {
        ...this.cardsContainer.options,
        draggable: { enabled: !isLocked },
        resizable: { enabled: !isLocked }
      };
    }
  }

  private restoreCanvasLockState(isLocked: boolean): void {
    if (this.canvasActions) {
      this.canvasActions.isCanvasLocked.set(isLocked);
      const lockActionIndex = this.canvasActions.quickActions.findIndex(a => a.clickAction === 'LOCK');
      if (lockActionIndex !== -1) {
        this.canvasActions.quickActions[lockActionIndex].name = isLocked ? 'lock-closed' : 'lock-open';
      }
      this.onToggleLockCanvas(isLocked);
    } else {
      setTimeout(() => {
        if (this.canvasActions) {
          this.restoreCanvasLockState(isLocked);
        }
      }, 100);
    }
  }

  onActionToggled(isOpen: boolean): void {
    this.isActionOpen.set(isOpen);
  }

  onSelectedCardsChange(event: { reportIds: string[], answerIds: string[] }): void {
    this.selectedReportIds.set(event.reportIds);
    this.selectedAnswerIds.set(event.answerIds);
  }

  onConversationsChange(conversations: { questions: any[], answers: any[] }): void {
    this.conversations.set(conversations);
  }

  onFreeFormCardsChange(freeFormCards: { cardId: string, assetId: string, content: string }[]): void {
    this.freeFormCards.set(freeFormCards);
  }

  onUnsavedFreeFormCardsChange(unsavedCards: { cardId: string, hasUnsavedChanges: boolean }[]): void {
    this.unsavedFreeFormCards.set(unsavedCards);
  }

  onTriggerCanvasSave(): void {
    if (this.canvasActions && this.canvasId) {
      this.canvasActions.onSaveCanvas(false, true);
    }
  }

  onCardRemoved(event: { reportId?: string, questionId?: string }): void {
    if (event.reportId) {
      this.addedReportIds.update(set => {
        const s = new Set(set);
        s.delete(event.reportId!);
        return s;
      });
    }
    if (event.questionId) {
      this.addedQuestionIds.update(set => {
        const s = new Set(set);
        s.delete(event.questionId!);
        return s;
      });
    }
  }

  handleAddToCanvas(event: any): void {
    if (this.cardsContainer) {
      this.cardsContainer.handleAddToCanvas(event);

      if (event?.items) {
        event.items.forEach((item: any) => {
          if (item.type === 'REPORT' && item.reportId) {
            this.addedReportIds.update(s => new Set([...s, item.reportId]));
          } else if (item.type === 'ANSWER' && item.questionId) {
            this.addedQuestionIds.update(s => new Set([...s, item.questionId]));
          }
        });
      }
    }
  }

  loadCanvasData(canvasId: string): void {
    if (!canvasId) return;

    this.isLoading.set(true);
    this.loadError.set(false);
    this.showAddContentModal.set(false);

    const savedConversations = this.copilotStorageService.loadConversations(canvasId);
    this.conversations.set(savedConversations || { questions: [], answers: [] });

    this.assetsApiService.getAssetById(canvasId, 'network-only')
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: (response: any) => {
          const canvasData = response?.data?.asset;
          if (!canvasData) {
            console.error('No canvas data found for ID:', canvasId);
            this.loadError.set(true);
            return;
          }

          this.canvasName = canvasData.name || '';
          this.canvasDescription = canvasData.description || '';

          if (canvasData.parameters?.isLocked) {
            this.restoreCanvasLockState(true);
          }

          if (canvasData.parameters?.cards) {
            const loadCards = () => {
              if (this.cardsContainer) {
                this.cardsContainer.cards.set(
                  canvasData.parameters.cards.map((card: any) => ({
                    ...card,
                    data: card.data || null,
                    chartConfigurations: card.chartConfigurations || null,
                    gridConfigurations: card.gridConfigurations || null
                  }))
                );
                this.updateAddedItemsTracking(canvasData.parameters.cards);
                this.loadFreeFormCardsData(canvasData.parameters.cards);
                return true;
              }
              return false;
            };

            if (!loadCards()) {
              const maxRetries = 5;
              let retries = 0;
              const retryInterval = setInterval(() => {
                retries++;
                if (loadCards() || retries >= maxRetries) {
                  clearInterval(retryInterval);
                  if (retries >= maxRetries && !this.cardsContainer) {
                    console.error('Failed to initialize cards container after multiple attempts');
                  }
                }
              }, 200);
            }
          } else {
            console.warn('No cards data found in canvas parameters');
          }
        },
        error: (error) => {
          console.error('Error loading canvas data:', error);
          this.loadError.set(true);
        }
      });
  }

  private loadFreeFormCardsData(cards: any[]): void {
    const freeFormCardsData: { cardId: string, assetId: string, content: string }[] = [];

    cards.forEach(card => {
      if (card.cardType === 'FREEFORM_CARD' && card.assetId) {
        this.loadingFreeFormCards.add(card.id);

        const cardIndex = this.cardsContainer?.cards()?.findIndex(c => c.id === card.id);
        if (cardIndex !== undefined && cardIndex !== -1) {
          this.cardsContainer.cards.update(cards => {
            const c = [...cards];
            c[cardIndex] = { ...c[cardIndex], isLoadingFreeForm: true, assetId: card.assetId };
            return c;
          });
        }

        this.assetsApiService.getAssetById(card.assetId, 'network-only').subscribe({
          next: (response: any) => {
            const assetData = response?.data?.asset;
            if (assetData?.parameters?.content) {
              const cardIndex = this.cardsContainer?.cards()?.findIndex(c => c.id === card.id);
              if (cardIndex !== undefined && cardIndex !== -1) {
                this.cardsContainer.cards.update(cards => {
                  const c = [...cards];
                  c[cardIndex] = { ...c[cardIndex], content: assetData.parameters.content, assetId: card.assetId, isLoadingFreeForm: false };
                  return c;
                });
              }

              freeFormCardsData.push({ cardId: card.id, assetId: card.assetId, content: assetData.parameters.content });
              this.freeFormCards.set([...freeFormCardsData]);
            } else {
              console.warn('No content found in asset data for card:', card.id);
              const cardIndex = this.cardsContainer?.cards()?.findIndex(c => c.id === card.id);
              if (cardIndex !== undefined && cardIndex !== -1) {
                this.cardsContainer.cards.update(cards => {
                  const c = [...cards];
                  c[cardIndex] = { ...c[cardIndex], isLoadingFreeForm: false };
                  return c;
                });
              }
            }

            this.loadingFreeFormCards.delete(card.id);
          },
          error: (error) => {
            console.error('Error loading free-form card asset via API for card:', card.id, error);
            const cardIndex = this.cardsContainer?.cards()?.findIndex(c => c.id === card.id);
            if (cardIndex !== undefined && cardIndex !== -1) {
              this.cardsContainer.cards.update(cards => {
                const c = [...cards];
                c[cardIndex] = { ...c[cardIndex], isLoadingFreeForm: false };
                return c;
              });
            }
            this.loadingFreeFormCards.delete(card.id);
          }
        });
      }
    });
  }

  updateAddedItemsTracking(cards: any[]): void {
    if (!this.cardsContainer) return;

    this.cardsContainer.addedReportIds.set(new Set<string>());
    this.cardsContainer.addedQuestionIds.set(new Set<string>());
    this.addedReportIds.set(new Set<string>());
    this.addedQuestionIds.set(new Set<string>());

    cards.forEach(card => {
      if (card.reportId) {
        this.cardsContainer.addedReportIds.update(s => new Set([...s, card.reportId]));
        this.addedReportIds.update(s => new Set([...s, card.reportId]));
      }
      if (card.questionId) {
        this.cardsContainer.addedQuestionIds.update(s => new Set([...s, card.questionId]));
        this.addedQuestionIds.update(s => new Set([...s, card.questionId]));
      }
    });
  }

  handleCopilotQuestion(question: string): void {
    const questionAsset = { parameters: { INPUT_TEXT: question } };

    const currentConversations = this.conversations();
    const questions = [...(currentConversations.questions || []), questionAsset];
    const answerIndex = (currentConversations.answers || []).length;
    const answers = [
      ...(currentConversations.answers || []),
      { isLoading: true, text: '', type: '', timestamp: '', tableData: null }
    ];

    this.conversations.set({ questions, answers });

    const payload = this.getPayloadForCopilot(question);

    this.dataCopilotService.canvasChat(payload).subscribe({
      next: (data) => {
        const answerData = data.data.find((item: any) => item.type === 'ANSWER');
        const currentTime = this.datePipe.transform(new Date(), 'MMMM d, YYYY, h:mm a');

        this.conversations.update(conv => {
          const updatedAnswers = [...conv.answers];
          updatedAnswers[answerIndex] = !answerData?.parameters?.DATA
            ? { isLoading: false, text: `⚠️ We could not generate an answer for this question, please try again later!`, type: 'simple', timestamp: currentTime, tableData: null, warning: true }
            : { isLoading: false, text: answerData.parameters.DATA, type: 'simple', timestamp: currentTime, tableData: null };
          return { ...conv, answers: updatedAnswers };
        });

        if (this.canvasId) {
          this.copilotStorageService.saveConversations(this.canvasId, this.conversations());
        }
      },
      error: (_err) => {
        const currentTime = this.datePipe.transform(new Date(), 'MMMM d, YYYY, h:mm a');
        this.conversations.update(conv => {
          const updatedAnswers = [...conv.answers];
          updatedAnswers[answerIndex] = { isLoading: false, warning: true, text: '⚠️ We could not generate an answer for this question, please try again later!', type: 'simple', timestamp: currentTime, tableData: null };
          return { ...conv, answers: updatedAnswers };
        });
      }
    });
  }

  private getPayloadForCopilot(messageText: string): any {
    const buildCardDataMapping = (reportIds: string[], answerIds: string[]) => {
      const reportDataMap: { [key: string]: any[] } = {};
      const answerDataMap: { [key: string]: any[] } = {};

      const cards = this.cardsContainer?.cards() || [];
      cards.forEach(card => {
        if (card.reportId && card.data?.reportVisualData) {
          reportDataMap[card.reportId] = card.data.reportVisualData;
        }
        if (card.answerId && card.data?.answerData) {
          answerDataMap[card.answerId] = card.data.answerData;
        }
      });

      return {
        reportDataMap: Object.keys(reportDataMap).length > 0 ? reportDataMap : undefined,
        answerDataMap: Object.keys(answerDataMap).length > 0 ? answerDataMap : undefined
      };
    };

    let reportIds = this.selectedReportIds();
    let answerIds = this.selectedAnswerIds();

    if (!this.canvasId && reportIds.length === 0 && answerIds.length === 0) {
      const cards = this.cardsContainer?.cards() || [];
      reportIds = cards.filter(card => card.reportId).map(card => card.reportId as string);
      answerIds = cards.filter(card => card.answerId).map(card => card.answerId as string);
    }

    const cardDataMapping = buildCardDataMapping(reportIds, answerIds);

    return {
      query: messageText,
      canvasId: this.canvasId,
      selectedReportIds: reportIds,
      selectedAnswerIds: answerIds,
      ...(cardDataMapping.reportDataMap && { reportDataMap: cardDataMapping.reportDataMap }),
      ...(cardDataMapping.answerDataMap && { answerDataMap: cardDataMapping.answerDataMap })
    };
  }
}
